import { useEffect, useState } from 'react';

import {
  LICENCE_CLASSES,
  ROUTING_DESTINATIONS,
  type RoutingDestination,
  type RuleFormState,
  parseMatchExpression,
} from '../lib/forms';

/**
 * Controlled form for creating / editing a routing rule.
 * Mirrors `validateCreateRule` in `lib/admin/routing-rules.ts` for client-side
 * checks; server is the source of truth.
 */
export function RuleForm({
  initial,
  submitLabel,
  busy,
  serverError,
  onSubmit,
  onCancel,
}: {
  readonly initial: RuleFormState;
  readonly submitLabel: string;
  readonly busy: boolean;
  readonly serverError: string | null;
  readonly onSubmit: (state: RuleFormState) => void;
  readonly onCancel: () => void;
}): JSX.Element {
  const [state, setState] = useState<RuleFormState>(initial);
  const [clientError, setClientError] = useState<string | null>(null);

  useEffect(() => {
    setState(initial);
    setClientError(null);
  }, [initial]);

  const onMatchChange = (raw: string): void => {
    const parsed = parseMatchExpression(raw);
    setState((p) => ({
      ...p,
      match_expression_raw: raw,
      match_expression_value: parsed.ok ? parsed.value : null,
    }));
  };

  const onSubmitClick = (): void => {
    if (state.name.trim().length === 0) {
      setClientError('name is required');
      return;
    }
    if (state.name.length > 200) {
      setClientError('name must be ≤200 chars');
      return;
    }
    if (state.match_expression_value === null) {
      setClientError('match_expression must be a JSON object');
      return;
    }
    if (state.destinations.length === 0) {
      setClientError('at least one destination is required');
      return;
    }
    setClientError(null);
    onSubmit(state);
  };

  const toggleDestination = (d: RoutingDestination): void => {
    setState((p) => {
      const has = p.destinations.includes(d);
      return {
        ...p,
        destinations: has ? p.destinations.filter((x) => x !== d) : [...p.destinations, d],
      };
    });
  };

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmitClick();
      }}
    >
      <Field label="Name">
        <input
          type="text"
          value={state.name}
          onChange={(e) => { setState((p) => ({ ...p, name: e.target.value })); }}
          required
          maxLength={200}
          className="form-input"
        />
      </Field>

      <Field
        label="Match expression (JSON)"
        hint="e.g. {&quot;tier_in&quot;:[&quot;S&quot;,&quot;A&quot;]} or {&quot;quality_min&quot;:60}"
      >
        <textarea
          value={state.match_expression_raw}
          onChange={(e) => { onMatchChange(e.target.value); }}
          rows={5}
          className="form-input font-mono text-2xs"
          spellCheck={false}
        />
        {state.match_expression_value === null && state.match_expression_raw !== '' && (
          <p className="text-2xs font-mono text-bad/90 mt-1">not a valid JSON object</p>
        )}
      </Field>

      <Field label="Destinations">
        <div className="flex gap-1.5 flex-wrap">
          {ROUTING_DESTINATIONS.map((d) => {
            const active = state.destinations.includes(d);
            return (
              <button
                key={d}
                type="button"
                onClick={() => { toggleDestination(d); }}
                className={active ? 'pill-ok cursor-pointer' : 'pill-dim cursor-pointer hover:bg-bg-line'}
              >
                {d}
              </button>
            );
          })}
        </div>
      </Field>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Licence class override (optional)">
          <select
            value={state.licence_class_override ?? ''}
            onChange={(e) => {
              const v = e.target.value;
              setState((p) => ({
                ...p,
                licence_class_override:
                  v === ''
                    ? null
                    : (v as RuleFormState['licence_class_override']),
              }));
            }}
            className="form-input"
          >
            <option value="">— no override —</option>
            {LICENCE_CLASSES.map((lc) => (
              <option key={lc} value={lc}>{lc}</option>
            ))}
          </select>
        </Field>

        <Field label="Priority (higher fires first)">
          <input
            type="number"
            value={state.priority ?? ''}
            onChange={(e) => {
              const v = e.target.value;
              setState((p) => ({ ...p, priority: v === '' ? null : Number(v) }));
            }}
            min={0}
            max={1_000_000}
            className="form-input"
          />
        </Field>
      </div>

      <Field label="">
        <label className="flex items-center gap-2 text-2xs font-mono text-ink-dim">
          <input
            type="checkbox"
            checked={state.active}
            onChange={(e) => { setState((p) => ({ ...p, active: e.target.checked })); }}
            className="accent-accent"
          />
          <span>active</span>
        </label>
      </Field>

      {(clientError ?? serverError) !== null && (
        <p className="text-2xs font-mono text-bad/90 border border-bad/30 bg-bad/10 px-3 py-2 rounded">
          {clientError ?? serverError}
        </p>
      )}

      <div className="flex justify-end gap-2 pt-2 border-t border-bg-line">
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="text-2xs font-mono uppercase tracking-wider px-3 py-1.5 rounded border border-bg-line hover:border-ink-dim hover:text-ink disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={busy}
          className="text-2xs font-mono uppercase tracking-wider px-3 py-1.5 rounded bg-accent hover:bg-accent-hover text-white disabled:opacity-50"
        >
          {busy ? '…' : submitLabel}
        </button>
      </div>
    </form>
  );
}

function Field({
  label,
  children,
  hint,
}: {
  readonly label: string;
  readonly children: React.ReactNode;
  readonly hint?: string;
}): JSX.Element {
  return (
    <label className="block">
      {label !== '' && (
        <span className="text-2xs font-mono text-ink-dim uppercase tracking-wider block mb-1">
          {label}
        </span>
      )}
      {children}
      {hint !== undefined && (
        <span className="text-2xs font-mono text-ink-muted block mt-1">{hint}</span>
      )}
    </label>
  );
}
